import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { BRACKET_IMAGE_URL } from '@/lib/constants';
import { Trophy, Vote, ZoomIn } from 'lucide-react';

export function HeroBanner() {
  const { user } = useCurrentUser();
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <section className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(247,147,26,0.12)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(247,147,26,0.06)_0%,transparent_50%)]" />

      {/* Animated grid lines */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(247,147,26,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(247,147,26,0.5) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center space-y-6 sm:space-y-8">
          {/* Top badge */}
          <div className="opacity-0 animate-fade-in-up stagger-1">
            <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs sm:text-sm px-4 py-1.5 tracking-wider uppercase font-medium">
              <Trophy className="w-3.5 h-3.5 mr-1.5" />
              March Madness Bracket Challenge
            </Badge>
          </div>

          {/* Main title */}
          <div className="space-y-3 opacity-0 animate-fade-in-up stagger-2">
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight">
              <span className="text-bitcoin-gradient">BITCOIN</span>
            </h1>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground">
              MAXI MADNESS
            </h1>
          </div>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed opacity-0 animate-fade-in-up stagger-3">
            Who is the ultimate Bitcoin maximalist? Round 2 is live!
            Vote in every matchup to decide the most based Bitcoiner in this epic bracket showdown.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 opacity-0 animate-fade-in-up stagger-4">
            <Button
              size="lg"
              asChild
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base px-8 h-12 rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
            >
              <Link to="/vote">
                <Vote className="w-5 h-5 mr-2" />
                Vote Round 2
              </Link>
            </Button>

            {!user && (
              <LoginArea className="w-full sm:w-auto" />
            )}
          </div>

          {/* Bracket image */}
          <div className="mt-8 sm:mt-12 opacity-0 animate-fade-in-up stagger-5">
            <Dialog>
              <DialogTrigger asChild>
                <button className="group relative mx-auto block max-w-4xl w-full rounded-2xl overflow-hidden border border-border/50 shadow-2xl shadow-black/30 transition-all duration-500 hover:border-primary/30 hover:shadow-primary/10 glow-pulse cursor-pointer">
                  {!imageLoaded && (
                    <div className="aspect-video bg-muted animate-pulse rounded-2xl" />
                  )}
                  <img
                    src={BRACKET_IMAGE_URL}
                    alt="Bitcoin Maxi Madness Bracket"
                    className={`w-full h-auto transition-all duration-500 group-hover:scale-[1.01] ${imageLoaded ? 'block' : 'hidden'}`}
                    onLoad={() => setImageLoaded(true)}
                    loading="eager"
                  />
                  {/* Zoom overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 backdrop-blur-sm rounded-full p-3">
                      <ZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-transparent border-0 shadow-none">
                <DialogTitle className="sr-only">Bitcoin Maxi Madness Bracket</DialogTitle>
                <DialogDescription className="sr-only">Full size view of the bracket tournament</DialogDescription>
                <img
                  src={BRACKET_IMAGE_URL}
                  alt="Bitcoin Maxi Madness Bracket - Full Size"
                  className="w-full h-auto max-h-[95vh] object-contain rounded-lg"
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
}
